import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { blogApi } from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeftIcon,
  CalendarIcon,
  UserIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'admin';

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getById(id);
      setPost(response.data);
    } catch (error) {
      toast.error('Failed to load blog post');
      console.error(error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPost();
  }, [id]);

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogApi.delete(post.id);
        toast.success('Blog post deleted successfully');
        navigate('/blog');
      } catch (error) {
        toast.error('Failed to delete blog post');
        console.error(error);
      }
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Blog
        </Link>
      </div>

      <article className="bg-white rounded-lg shadow overflow-hidden">
        {post.imageUrl && (
          <div className="aspect-video w-full overflow-hidden bg-gray-200">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>

          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-center text-sm text-gray-500 space-x-4">
              <div className="flex items-center">
                <UserIcon className="h-4 w-4 mr-1" />
                Admin
              </div>
              <div className="flex items-center">
                <CalendarIcon className="h-4 w-4 mr-1" />
                {post.publishedAt
                  ? format(new Date(post.publishedAt), 'MMMM d, yyyy')
                  : format(new Date(post.createdAt), 'MMMM d, yyyy')}
              </div>
            </div>

            {isAdmin && (
              <div className="flex space-x-2">
                <Link
                  to={`/blog/edit/${post.id}`}
                  className="text-blue-600 hover:text-blue-800 p-2"
                  title="Edit"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </Link>
                <button
                  onClick={handleDelete}
                  className="text-red-600 hover:text-red-800 p-2"
                  title="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            )}
          </div>

          {post.tags && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.split(',').map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}

          <div className="prose prose-lg max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </div>
        </div>
      </article>

      <div className="mt-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Blog
        </Link>
      </div>
    </div>
  );
};

export default BlogPost;
